import { useState, useEffect, useRef, useCallback } from "react";
import { Send, ArrowLeft, Loader2, Bot, Users, MessageSquare, Activity, TrendingUp, ShoppingCart, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { API_URL } from "@/config/api";
import { authFetch } from "@/utils/auth-fetch";
import { useNavigate, useParams } from "react-router-dom";
import { crewAgents } from "@/data/crew-agents-ordered";
import { format } from "date-fns";
import MessageComponent from "./components/MessageComponent";
import { Message, MessageType, Conversation } from "./types";
import { agentConfigs } from "./config/agentConfigs";
import { useSite } from "@/contexts/SiteContext";
import { io, Socket } from "socket.io-client";
import { useOnboardingTracking } from "@/hooks/useOnboardingTracking";

// Agent avatar mapping
const getAgentAvatar = (agentName: string): string => {
  const avatarMap: Record<string, string> = {
    'page-chatbot-agent': '/crew-agents/chatbot.jpg',
    'customer-intent-agent': '/crew-agents/customer-intent.jpg',
    'page-semantic-intent-agent': '/crew-agents/content-analyzer.jpg',
    'site-crawler-agent': '/crew-agents/crawler.jpg',
    'voice-assistant-agent': '/crew-agents/voice.jpg',
    'orchestration-agent': '/crew-agents/orchestrator.jpg',
    'page-role-agent': '/crew-agents/page-role.jpg',
    'adjacent-topics-agent': '/crew-agents/topics.jpg',
    'competitive-analysis-agent': '/crew-agents/competitive-analysis.jpg',
    'intent-based-recommender-agent': '/crew-agents/recommender.jpg',
    'keyword-extractor-agent': '/crew-agents/keyword-extractor.jpg',
    'narrative-presentation-agent': '/crew-agents/narrative.jpg',
    'onboarding-agent': '/crew-agents/onboarding.jpg',
    'question-suggestion-agent': '/crew-agents/questions.jpg',
    'session-summarizer-agent': '/crew-agents/summarizer.jpg',
    'site-sector-analyzer': '/crew-agents/sector-analyzer.jpg',
    'site-sector-classifier': '/crew-agents/sector-classifier.jpg',
    'style-suggestion-agent': '/crew-agents/style.jpg',
    'auto-evaluator-agent': '/crew-agents/evaluator.jpg',
    'conductor-agent': '/crew-agents/conductor.jpg',
    'insights-hub-agent': '/crew-agents/insights-hub.jpg',
    'web-analyst': '/crew-agents/analyst.jpg',
    'marketing-expert': '/crew-agents/marketing.jpg',
    'cro-expert': '/crew-agents/cro.jpg',
    'ux-researcher': '/crew-agents/ux.jpg',
    'technical-optimizer': '/crew-agents/technical.jpg',
    'conversion-specialist': '/crew-agents/conversion.jpg',
    'chatbot': '/crew-agents/chatbot.jpg',
    'site-crawler': '/crew-agents/crawler.jpg',
  };
  
  // Direct lookup
  if (avatarMap[agentName]) {
    return avatarMap[agentName];
  }
  
  // Try without -agent suffix
  const withoutAgent = agentName.replace(/-agent$/, '');
  if (avatarMap[withoutAgent]) {
    return avatarMap[withoutAgent];
  }
  
  // Try with -agent suffix
  const withAgent = `${agentName}-agent`;
  if (avatarMap[withAgent]) {
    return avatarMap[withAgent];
  }
  
  // Default fallback
  return '/crew-agents/default.jpg';
};

// Group conversation types
interface GroupMessage {
  id: string;
  groupId: string;
  agentName: string;
  message: string;
  messageType: 'notification' | 'insight' | 'action' | 'status' | 'error';
  priority: 'high' | 'medium' | 'low';
  timestamp: string;
  metadata?: {
    siteId?: string;
    sessionId?: string;
    confidence?: number;
    actionData?: any;
  };
}

const UnifiedAgentHub = () => {
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const { selectedSite } = useSite();
  const { trackOnboardingStep } = useOnboardingTracking();
  const [activeTab, setActiveTab] = useState<'direct' | 'group'>('direct');
  const [hasTrackedAgentChat, setHasTrackedAgentChat] = useState(false);
  
  // Direct message state
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [typing, setTyping] = useState(false);
  
  // Group conversation state
  const [groupMessages, setGroupMessages] = useState<GroupMessage[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('intelligence-feed');
  const [socket, setSocket] = useState<Socket | null>(null);
  
  // Real-time data state (for web-analyst)
  const [realtimeData, setRealtimeData] = useState<any>(null);
  const [dataLoading, setDataLoading] = useState(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Get agent info
  const agent = crewAgents.find(a => a.id === agentId);
  const agentConfig = agentConfigs[agentId || ""];
  
  // Group channels
  const groupChannels = [
    { id: 'intelligence-feed', name: 'Intelligence Feed', icon: Activity },
    { id: 'conversations', name: 'Customer Conversations', icon: MessageSquare },
    { id: 'support-team', name: 'Support Team', icon: HelpCircle },
    { id: 'sales-insights', name: 'Sales Insights', icon: TrendingUp },
    { id: 'shopping-behavior', name: 'Shopping Behavior', icon: ShoppingCart },
  ];

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, groupMessages]);

  // Initialize WebSocket for group conversations
  useEffect(() => {
    if (activeTab === 'group') {
      const socketUrl = API_URL.replace('/api', '');
      const newSocket = io(socketUrl, {
        path: '/socket.io/',
        transports: ['websocket', 'polling'],
        withCredentials: true,
      });

      newSocket.on('connect', () => {
        console.log('Connected to WebSocket');
        // Join selected site's room
        if (selectedSite?.id) {
          newSocket.emit('join-site', selectedSite.id);
        }
      });

      newSocket.on('agent-conversation', (data: any) => {
        if (data.channel === selectedGroup) {
          const groupMsg: GroupMessage = {
            id: data.id || `msg-${Date.now()}`,
            groupId: data.channel,
            agentName: data.agentName,
            message: data.message,
            messageType: data.messageType || 'notification',
            priority: data.priority || 'medium',
            timestamp: data.timestamp || new Date().toISOString(),
            metadata: data.metadata,
          };
          setGroupMessages(prev => [...prev, groupMsg]);
        }
      });

      setSocket(newSocket);

      return () => {
        newSocket.disconnect();
      };
    }
  }, [activeTab, selectedGroup, selectedSite]);

  // Initialize direct conversation
  useEffect(() => {
    if (agentId && agent && activeTab === 'direct') {
      initializeConversation();
    }
  }, [agentId, activeTab]);

  // Fetch real-time data for web-analyst
  const fetchRealtimeData = useCallback(async (context?: string) => {
    if (agentId !== 'web-analyst' || !selectedSite?.id) return;
    
    setDataLoading(true);
    try {
      // Parse context to determine date range
      let dateRange = 'last7days'; // default
      if (context?.toLowerCase().includes('today')) {
        dateRange = 'today';
      } else if (context?.toLowerCase().includes('yesterday')) {
        dateRange = 'yesterday';
      } else if (context?.toLowerCase().includes('week')) {
        dateRange = 'last7days';
      } else if (context?.toLowerCase().includes('month')) {
        dateRange = 'last30days';
      }

      const response = await authFetch(`/analytics/matomo/realtime?siteUrl=${encodeURIComponent(selectedSite.url || selectedSite.domain)}&range=${dateRange}`);
      if (!response.ok) throw new Error('Failed to fetch analytics');
      
      const data = await response.json();
      setRealtimeData(data);
      
      // Return data for agent to use
      return data;
    } catch (error) {
      console.error('Failed to fetch real-time data:', error);
      return null;
    } finally {
      setDataLoading(false);
    }
  }, [agentId, selectedSite]);

  const initializeConversation = async () => {
    try {
      const response = await authFetch(`/agent-conversations/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          agentId,
          userId: localStorage.getItem("user_id"),
          siteId: selectedSite?.id,
        }),
      });

      if (!response.ok) throw new Error("Failed to start conversation");

      const data = await response.json();
      setConversation(data.conversation);

      // Add welcome message
      const welcomeMessage: Message = {
        id: `welcome-${Date.now()}`,
        conversationId: data.conversation.id,
        agentId: agentId!,
        role: "assistant",
        content: agentConfig?.welcomeMessage || `Hi! I'm ${agent?.name}. ${agent?.shortDescription}. How can I help you today?`,
        type: MessageType.TEXT,
        timestamp: new Date().toISOString(),
      };
      setMessages([welcomeMessage]);
    } catch (error) {
      console.error("Failed to initialize conversation:", error);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || !conversation) return;

    // Track "talk_to_agents" onboarding step on first message
    if (!hasTrackedAgentChat && selectedSite) {
      trackOnboardingStep('talk_to_agents', { 
        agentId, 
        timestamp: new Date().toISOString() 
      });
      setHasTrackedAgentChat(true);
    }

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      conversationId: conversation.id,
      agentId: agentId!,
      role: "user",
      content: input,
      type: MessageType.TEXT,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setTyping(true);

    try {
      // For web-analyst, fetch real-time data based on message context
      let contextData = null;
      if (agentId === 'web-analyst') {
        contextData = await fetchRealtimeData(input);
      }

      const response = await authFetch(`/agent-conversations/message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          conversationId: conversation.id,
          message: input,
          agentId,
          siteId: selectedSite?.id,
          contextData, // Include real-time data for web-analyst
        }),
      });

      if (!response.ok) throw new Error("Failed to send message");

      const data = await response.json();
      
      if (data.messages && data.messages.length > 0) {
        setMessages(prev => [...prev, ...data.messages]);
      }

      if (data.handoff) {
        handleAgentHandoff(data.handoff);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        conversationId: conversation.id,
        agentId: agentId!,
        role: "assistant",
        content: "I'm having trouble processing your request. Please try again.",
        type: MessageType.TEXT,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setTyping(false);
    }
  };

  const handleAgentHandoff = async (handoff: { targetAgentId: string; context: any }) => {
    const handoffMessage: Message = {
      id: `handoff-${Date.now()}`,
      conversationId: conversation!.id,
      agentId: agentId!,
      role: "system",
      content: `Transferring you to ${handoff.targetAgentId} for specialized assistance...`,
      type: MessageType.HANDOFF,
      metadata: handoff,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, handoffMessage]);

    setTimeout(() => {
      navigate(`/insights/hub/${handoff.targetAgentId}`, {
        state: { handoffContext: handoff.context }
      });
    }, 1500);
  };

  if (!agent) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Agent not found</h2>
          <Button onClick={() => navigate("/crew")} className="mt-4">
            Back to Crew
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 pb-4 border-b">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/crew")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>

          <Avatar className="h-10 w-10">
            <AvatarImage src={getAgentAvatar(agent.id)} />
            <AvatarFallback>
              <Bot className="h-5 w-5" />
            </AvatarFallback>
          </Avatar>

          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              {agent.name}
              <Badge variant="outline" className="text-xs">
                {agent.category[0]}
              </Badge>
            </h1>
            <p className="text-sm text-muted-foreground">
              {agent.shortDescription}
            </p>
          </div>
        </div>

        {/* Real-time data indicator for web-analyst */}
        {agentId === 'web-analyst' && realtimeData && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Activity className="h-4 w-4" />
            <span>Live data: {realtimeData.visitors || 0} visitors</span>
          </div>
        )}
      </div>

      {/* Tabs for Direct Messages and Group Conversations */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'direct' | 'group')} className="flex-1 flex flex-col">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="direct" className="flex items-center gap-2">
            <Bot className="h-4 w-4" />
            Direct Message
          </TabsTrigger>
          <TabsTrigger value="group" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Group Conversations
          </TabsTrigger>
        </TabsList>

        {/* Direct Message Tab */}
        <TabsContent value="direct" className="flex-1 flex flex-col">
          <ScrollArea className="flex-1 px-4">
            <div className="space-y-4 py-4">
              {messages.map((message) => (
                <MessageComponent key={message.id} message={message} agent={agent} />
              ))}
              
              {typing && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">{agent.name} is typing...</span>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input Area */}
          <div className="mt-4 pt-4 border-t">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendMessage();
              }}
              className="flex gap-2"
            >
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={`Ask ${agent.name} anything...`}
                disabled={loading || typing}
                className="flex-1"
              />
              <Button
                type="submit"
                disabled={!input.trim() || loading || typing}
              >
                <Send className="h-4 w-4 mr-2" />
                Send
              </Button>
            </form>

            {/* Quick Actions */}
            {agentConfig?.quickActions && (
              <div className="flex gap-2 mt-2 flex-wrap">
                {agentConfig.quickActions.map((action, idx) => (
                  <Button
                    key={idx}
                    variant="outline"
                    size="sm"
                    onClick={() => setInput(action)}
                  >
                    {action}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Group Conversations Tab */}
        <TabsContent value="group" className="flex-1 flex">
          <div className="flex flex-1 gap-4">
            {/* Channel List */}
            <div className="w-64 space-y-2">
              {groupChannels.map((channel) => (
                <Button
                  key={channel.id}
                  variant={selectedGroup === channel.id ? "default" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => setSelectedGroup(channel.id)}
                >
                  <channel.icon className="h-4 w-4 mr-2" />
                  {channel.name}
                </Button>
              ))}
            </div>

            {/* Messages Area */}
            <div className="flex-1 flex flex-col">
              <Card className="flex-1 flex flex-col">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {groupChannels.find(c => c.id === selectedGroup)?.icon && (
                      <div className="h-5 w-5">
                        {(() => {
                          const Icon = groupChannels.find(c => c.id === selectedGroup)?.icon;
                          return Icon ? <Icon className="h-5 w-5" /> : null;
                        })()}
                      </div>
                    )}
                    {groupChannels.find(c => c.id === selectedGroup)?.name}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <ScrollArea className="flex-1">
                    <div className="space-y-4">
                      {groupMessages
                        .filter(msg => msg.groupId === selectedGroup)
                        .map((msg) => (
                          <div key={msg.id} className="flex gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={getAgentAvatar(msg.agentName)} />
                              <AvatarFallback>
                                <Bot className="h-4 w-4" />
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-sm">
                                  {msg.agentName.replace(/-agent$/, '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </span>
                                <Badge variant={
                                  msg.priority === 'high' ? 'destructive' : 
                                  msg.priority === 'medium' ? 'default' : 
                                  'secondary'
                                } className="text-xs">
                                  {msg.priority}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {format(new Date(msg.timestamp), 'HH:mm')}
                                </span>
                              </div>
                              <p className="text-sm">{msg.message}</p>
                              {msg.metadata?.actionData && (
                                <Button
                                  variant="link"
                                  size="sm"
                                  className="mt-1 p-0 h-auto"
                                  onClick={() => {
                                    // Handle action based on actionType
                                    const { actionType, actionPayload } = msg.metadata.actionData;
                                    if (actionType === 'view-session') {
                                      navigate(`/sessions/${actionPayload.sessionId}`);
                                    } else if (actionType === 'view-crawl-status') {
                                      navigate(`/site/${actionPayload.siteId}/crawl/${actionPayload.crawlId}`);
                                    }
                                  }}
                                >
                                  View Details →
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UnifiedAgentHub;